/*!
 * Oneskin Multi Column — vanilla JS controller for the Multi-column section
 * (responsive native carousel + optional reveal animation).
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'multi-column.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces:
 *   frontend/scripts/sections/multi-column.js
 *   frontend/scripts/lib/animation/multi-column.js
 *   frontend/scripts/lib/carousel.js (Swiper wrapper → native scroll-snap)
 */
;(() => {
  'use strict'

  if (window.__oneskinMultiColumn) return
  window.__oneskinMultiColumn = true

  const SECTION_TYPE = 'multi-column'

  function shouldAnimate(node) {
    return (
      node.classList.contains('animation') &&
      !document.documentElement.classList.contains('prefers-reduced-motion')
    )
  }

  function delayOffset(node, selectorList, offsetStart = 0) {
    let offset = offsetStart
    selectorList.forEach((selector) => {
      node.querySelectorAll(selector).forEach((item) => {
        item.style.setProperty('--delay-offset-multiplier', String(offset))
        offset++
      })
    })
  }

  function intersectionWatcher(node) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(node)

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  function atBreakpointChange(breakpoint, callback) {
    let underBreakpoint = window.innerWidth < breakpoint

    const widthWatcher = () => {
      const isUnder = window.innerWidth < breakpoint
      if (isUnder !== underBreakpoint) {
        underBreakpoint = isUnder
        callback()
      }
    }

    window.addEventListener('resize', widthWatcher)

    return {
      destroy() {
        window.removeEventListener('resize', widthWatcher)
      },
    }
  }

  function initResponsiveCarousel(viewport, options = {}) {
    if (!viewport) return null

    const track = viewport.querySelector('[data-multi-column-track]') || viewport
    const slides = Array.from(track.querySelectorAll('[data-multi-column-slide]'))
    if (!slides.length) return null

    track.classList.remove('grid')
    track.classList.add(
      'multi-column__track--native',
      'flex',
      'gap-0',
      'overflow-x-auto',
      'scroll-smooth',
      'snap-x',
      'snap-mandatory',
      '[scrollbar-width:none]',
      '[-ms-overflow-style:none]',
      '[&::-webkit-scrollbar]:hidden'
    )

    const breakpointEntries = Object.keys(options.breakpoints || {})
      .map(Number)
      .sort((a, b) => a - b)

    function activeConfig() {
      const width = window.innerWidth
      let config = {
        slidesPerView: options.slidesPerView,
        spaceBetween: options.spaceBetween,
      }
      breakpointEntries.forEach((bp) => {
        if (width >= bp) config = Object.assign({}, config, options.breakpoints[bp])
      })
      return config
    }

    function applyConfig() {
      const { slidesPerView, spaceBetween } = activeConfig()
      const gap = spaceBetween == null ? 0 : spaceBetween

      if (spaceBetween != null) track.style.gap = `${gap}px`

      if (slidesPerView) {
        const width = `calc((100% - ${(slidesPerView - 1) * gap}px) / ${slidesPerView})`
        slides.forEach((slide) => {
          slide.style.width = width
          slide.style.flexShrink = '0'
          slide.style.scrollSnapAlign = 'start'
        })
      }
    }

    const resizeHandler = () => applyConfig()
    window.addEventListener('resize', resizeHandler)
    applyConfig()

    return {
      destroy() {
        window.removeEventListener('resize', resizeHandler)
        track.classList.remove(
          'multi-column__track--native',
          'flex',
          'gap-0',
          'overflow-x-auto',
          'scroll-smooth',
          'snap-x',
          'snap-mandatory',
          '[scrollbar-width:none]',
          '[-ms-overflow-style:none]',
          '[&::-webkit-scrollbar]:hidden'
        )
        track.classList.add('grid')
        track.style.removeProperty('gap')
        slides.forEach((slide) => {
          slide.style.removeProperty('width')
          slide.style.removeProperty('flex-shrink')
          slide.style.removeProperty('scroll-snap-align')
        })
      },
    }
  }

  function sliderEnabledAtWidth(container, width) {
    const { sliderOnMobile, sliderOnTablet, sliderOnDesktop } = container.dataset
    if (width < 720) return sliderOnMobile === 'true'
    if (width < 960) return sliderOnTablet === 'true'
    return sliderOnDesktop === 'true'
  }

  function initMultiColumn(container) {
    const viewport = container.querySelector('[data-multi-column-carousel]')
    let carousel = null
    let revealAnimation = null

    const mobilePerView = parseFloat(container.dataset.mobileColumnsPerView || '2') * 1.1
    const tabletRaw = container.dataset.tabletColumnsPerView || '2'
    const desktopRaw = container.dataset.desktopColumnsPerView || '4'
    const tabletPerView = tabletRaw === '1' ? 1.4 : parseFloat(tabletRaw) * 1.1
    const desktopPerView = desktopRaw === '1' ? 1.4 : parseFloat(desktopRaw) * 1.1
    const mobileGap = parseFloat(container.dataset.columnSpacingMobile || '12')
    const tabletGap = parseFloat(container.dataset.columnSpacingTablet || '12')
    const desktopGap = parseFloat(container.dataset.columnSpacingDesktop || '12')

    function createCarousel() {
      if (carousel || !viewport) return
      if (!sliderEnabledAtWidth(container, window.innerWidth)) return

      carousel = initResponsiveCarousel(viewport, {
        slidesPerView: mobilePerView,
        spaceBetween: mobileGap,
        breakpoints: {
          720: {
            slidesPerView: tabletPerView,
            spaceBetween: tabletGap,
          },
          960: {
            slidesPerView: desktopPerView,
            spaceBetween: desktopGap,
          },
        },
      })
    }

    function destroyCarousel() {
      carousel?.destroy()
      carousel = null
    }

    function syncCarousel() {
      if (sliderEnabledAtWidth(container, window.innerWidth)) {
        if (!carousel) createCarousel()
      } else {
        destroyCarousel()
      }
    }

    syncCarousel()

    const breakpointHandler = atBreakpointChange(720, syncCarousel)
    const breakpointHandlerTablet = atBreakpointChange(960, syncCarousel)

    if (shouldAnimate(container)) {
      delayOffset(container, [
        '.animation--section-introduction > *',
        '.multi-column__grid-item',
      ])
      revealAnimation = intersectionWatcher(container)
    }

    return {
      unload() {
        breakpointHandler.destroy()
        breakpointHandlerTablet.destroy()
        destroyCarousel()
        revealAnimation?.destroy()
      },
    }
  }

  const instances = new WeakMap()

  function initContainer(container) {
    if (!container || instances.has(container)) return
    if (container.dataset.sectionType !== SECTION_TYPE) return
    instances.set(container, initMultiColumn(container))
  }

  function unloadContainer(container) {
    instances.get(container)?.unload?.()
    instances.delete(container)
  }

  function autoInit(root = document) {
    root.querySelectorAll(`[data-section-type="${SECTION_TYPE}"]`).forEach(initContainer)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  function containerFor(event) {
    const sectionId = event.detail?.sectionId
    return sectionId ? document.querySelector(`[data-section-id="${sectionId}"]`) : null
  }

  document.addEventListener('shopify:section:load', (event) => {
    const container = containerFor(event)
    if (container) initContainer(container)
    else autoInit(event.target)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const container = containerFor(event)
    if (container) unloadContainer(container)
  })
})()
